import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Feedback Modal Hook - Controls modal visibility based on viewport events
 * 
 * Features:
 * - Shows modal after artifact is visible for 2 seconds
 * - Implements daily limits and probability-based showing
 * - Handles animation state for repeated visits
 * - Prevents showing modal on same artifact multiple times per day
 * - Requires minimum number of artifacts viewed before enabling modal
 */

const FEEDBACK_MODAL_ENABLED = false;
const STORAGE_KEY = 'feedbackModal';
const MAX_SHOWS_PER_DAY = 5;
const SHOW_PROBABILITY = 0.5;
const VISIBILITY_DELAY = 2000;
const MIN_ARTIFACTS_VIEWED = 5;

const DEFAULT_STORAGE_DATA = {
  showsToday: 0,
  lastShowDate: null,
  lastShowTime: 0,
  lastArtifactIds: [], // Artifacts where modal was shown
  totalShows: 0,
  artifactsViewed: 0,
  viewedArtifactIds: [] // All viewed artifacts (for unique counting)
};

export const useFeedbackModal = (hasAnimationCompleted = false) => {
  const [shouldShow, setShouldShow] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimationStarted, setHasAnimationStarted] = useState(false);
  
  const timeoutRef = useRef(null);
  const artifactIdRef = useRef(null);
  const visibilityEventListenerRef = useRef(null);

  const getStorageData = useCallback(() => {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      const parsed = data ? JSON.parse(data) : null;

      return parsed || DEFAULT_STORAGE_DATA;
    } catch {
      return DEFAULT_STORAGE_DATA;
    }
  }, []);

  const updateStorageData = useCallback((updates) => {
    const currentData = getStorageData();
    const newData = { ...currentData, ...updates };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newData));
    return newData;
  }, [getStorageData]);

  const resetDailyDataIfNeeded = useCallback((currentData) => {
    const today = new Date().toDateString();
    
    if (currentData.lastShowDate !== today) {
      const resetData = {
        showsToday: 0,
        lastShowDate: today,
        lastArtifactIds: [],
        artifactsViewed: 0,
        viewedArtifactIds: []
      };
      updateStorageData(resetData);
      return { ...currentData, ...resetData };
    }
    
    return currentData;
  }, [updateStorageData]);

  const canShowModal = useCallback((artifactId, data) => {
    if (!FEEDBACK_MODAL_ENABLED) {
      return false;
    }

    // Check daily limit
    if (data.showsToday >= MAX_SHOWS_PER_DAY) {
      return false;
    }

    // Check minimum artifacts viewed before showing modal
    if ((data.artifactsViewed || 0) < MIN_ARTIFACTS_VIEWED) {
      return false;
    }

    // Prevent showing on same artifact if it was already shown
    if (data.lastArtifactIds && data.lastArtifactIds.includes(artifactId)) {
      return false;
    }

    // Apply probability filter
    return Math.random() < SHOW_PROBABILITY;
  }, []);

  // Starts visibility timer when artifact enters viewport
  const startVisibilityTimer = useCallback((artifactId) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    timeoutRef.current = setTimeout(() => {
      if (artifactIdRef.current === artifactId) {
        let data = getStorageData();
        data = resetDailyDataIfNeeded(data);
        
        const viewedIds = data.viewedArtifactIds || [];
        
        // Only count if this artifact hasn't been viewed before
        if (!viewedIds.includes(artifactId)) {
          const updatedData = {
            artifactsViewed: (data.artifactsViewed || 0) + 1,
            viewedArtifactIds: [...viewedIds, artifactId]
          };
          
          updateStorageData(updatedData);
          data = { ...data, ...updatedData };

          // Check if we can show modal with updated data
          if (canShowModal(artifactId, data)) {
            setShouldShow(true);
            setIsVisible(true);
            setHasAnimationStarted(true);
            
            // Track modal show
            const modalShownIds = data.lastArtifactIds || [];
            const today = new Date().toDateString();
            
            updateStorageData({
              showsToday: data.lastShowDate === today ? data.showsToday + 1 : 1,
              lastShowDate: today,
              lastShowTime: Date.now(),
              lastArtifactIds: modalShownIds.includes(artifactId) 
                ? modalShownIds 
                : [...modalShownIds, artifactId],
              totalShows: (data.totalShows || 0) + 1
            });
          }
        }
      }
      
      timeoutRef.current = null;
    }, VISIBILITY_DELAY);
  }, [getStorageData, updateStorageData, resetDailyDataIfNeeded, canShowModal]);

  // Cancels the visibility timer
  const cancelVisibilityTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Handles visibility events from viewport tracking
  const handleVisibilityEvent = useCallback((event) => {
    const action = event.detail?.action;
    const hasParam = 'isVisible' in (event?.detail?.params || {});
    
    if (action !== 'visibilityStatus' || !hasParam) {
      cancelVisibilityTimer();
      return;
    }

    const isVisible = event.detail.params.isVisible;
    
    if (isVisible && artifactIdRef.current) {
      startVisibilityTimer(artifactIdRef.current);
    } else {
      cancelVisibilityTimer();
    }
  }, [startVisibilityTimer, cancelVisibilityTimer]);

  // Sets up visibility tracking for modal trigger
  const triggerModal = useCallback((artifactId) => {
    artifactIdRef.current = artifactId;

    // Always set up event listeners for view tracking, regardless of modal conditions
    if (visibilityEventListenerRef.current) {
      window.removeEventListener('appEvent', visibilityEventListenerRef.current);
    }

    visibilityEventListenerRef.current = handleVisibilityEvent;
    window.addEventListener('appEvent', handleVisibilityEvent);

    // Show immediately if animation was already completed on this artifact
    // and modal conditions are met
    if (hasAnimationCompleted) {
      let data = getStorageData();
      data = resetDailyDataIfNeeded(data);
      
      if (canShowModal(artifactId, data)) {
        setShouldShow(true);
        setIsVisible(true);
        return undefined;
      }
    }

    // Return cleanup function
    return () => {
      cancelVisibilityTimer();
      if (visibilityEventListenerRef.current) {
        window.removeEventListener('appEvent', visibilityEventListenerRef.current);
        visibilityEventListenerRef.current = null;
      }
      artifactIdRef.current = null;
    };
  }, [handleVisibilityEvent, cancelVisibilityTimer, hasAnimationCompleted, getStorageData, resetDailyDataIfNeeded, canShowModal]);

  const hideModal = useCallback(() => {
    setIsVisible(false);
    setTimeout(() => {
      setShouldShow(false);
    }, 400);
  }, []);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      cancelVisibilityTimer();
      if (visibilityEventListenerRef.current) {
        window.removeEventListener('appEvent', visibilityEventListenerRef.current);
        visibilityEventListenerRef.current = null;
      }
    };
  }, [cancelVisibilityTimer]);

  return {
    shouldShow,
    isVisible,
    hasAnimationStarted,
    triggerModal,
    hideModal
  };
};
